import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import Navbar from "../../components/navbar/Navbar";
import { Link } from "react-router-dom"; 

const SearchResultsPage = () => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const searchTerm = searchParams.get('q') || ''; 

    const [serviceResults, setServiceResults] = useState([]);
    const [vendorResults, setVendorResults] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const token = localStorage.getItem("token");

    const fetchResults = useCallback(async () => {
        if (!searchTerm) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const servicePromise = axiosInstance.get(`/users/services?search=${encodeURIComponent(searchTerm)}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const vendorPromise = axiosInstance.get(`/users/vendors/search?q=${encodeURIComponent(searchTerm)}`, {
                 headers: { Authorization: `Bearer ${token}` },
            });

            const [serviceRes, vendorRes] = await Promise.all([servicePromise, vendorPromise]);
            
            // Robust data extraction for services
            const services = serviceRes.data?.services || serviceRes.data?.data || serviceRes.data || [];
            // Robust data extraction for vendors
            const vendors = vendorRes.data?.vendors || vendorRes.data?.data || vendorRes.data || [];
            
            setServiceResults(Array.isArray(services) ? services : []);
            setVendorResults(Array.isArray(vendors) ? vendors : []);

        } catch (err) {
            console.error("Error fetching search results:", err);
            setServiceResults([]);
            setVendorResults([]);
        } finally {
            setIsLoading(false);
        }
    }, [searchTerm, token]);

    useEffect(() => {
        fetchResults();
    }, [fetchResults]);

    const renderServiceCard = (service) => (
        <Link 
            key={service._id} 
            to={`/services/${service._id}`} 
            className="bg-white shadow-md rounded-lg p-4 w-full md:w-80 hover:shadow-lg transition cursor-pointer"
        >
            <h4 className="text-lg font-semibold text-blue-700">{service.title}</h4>
            <p className="text-sm text-gray-600 mt-1">{service.categoryName || 'General Service'}</p>
        </Link>
    );

    const renderVendorCard = (vendor) => (
        <Link 
            key={vendor._id} 
            to={`/experts/${vendor._id}`} 
            className="bg-white shadow-md rounded-lg p-4 w-full md:w-80 hover:shadow-lg transition cursor-pointer border-l-4 border-green-500"
        >
            <h4 className="text-lg font-semibold text-gray-800">{vendor.firstName} {vendor.lastName}</h4>
            <p className="text-sm text-gray-600 mt-1">{vendor.expertise || 'Expert Professional'}</p>
        </Link>
    );

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-[#f4f8fc] p-8">
                <h2 className="text-4xl font-extrabold text-blue-800 mb-2">
                    Search Results
                </h2>
                <p className="text-xl text-gray-600 mb-8">
                    Showing results for: <span className="font-mono bg-yellow-100 px-2 py-0.5 rounded-md">{searchTerm}</span>
                </p>

                {isLoading ? (
                    <div className="text-center py-10">
                        <p className="text-blue-500 text-lg">Searching for services and experts...</p>
                    </div>
                ) : (
                    <div className="space-y-12">
                        {/* Service Results Section */}
                        <section>
                            <h3 className="text-2xl font-semibold text-gray-700 mb-4 border-b pb-2">
                                Services & Categories ({serviceResults.length})
                            </h3>
                            {serviceResults.length === 0 ? (
                                <p className="text-gray-500">No matching services or categories found.</p>
                            ) : (
                                <div className="flex flex-wrap gap-6 justify-start">
                                    {serviceResults.map(renderServiceCard)}
                                </div>
                            )}
                        </section>

                        {/* Vendor/Expert Results Section */}
                        <section>
                            <h3 className="text-2xl font-semibold text-gray-700 mb-4 border-b pb-2">
                                Experts & Vendors ({vendorResults.length})
                            </h3>
                            {vendorResults.length === 0 ? (
                                <p className="text-gray-500">No matching experts found.</p>
                            ) : (
                                <div className="flex flex-wrap gap-6 justify-start">
                                    {vendorResults.map(renderVendorCard)}
                                </div>
                            )}
                        </section>
                        
                        {serviceResults.length === 0 && vendorResults.length === 0 && (
                            <div className="text-center py-10">
                                <p className="text-gray-700 text-xl">No results found for "{searchTerm}".</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default SearchResultsPage;